import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'
import { captureReferralCode } from '../../lib/referrals'
import { Mail, Lock, Eye, EyeOff, User } from 'lucide-react'
import AuthShell from '../../components/AuthShell'

export default function SignupPage() {
  const { signUpWithEmail, signInWithGoogle } = useAuth()
  const navigate = useNavigate()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [loading, setLoading] = useState(false)
  const [paused, setPaused] = useState(false)
  useEffect(() => {
    captureReferralCode()
    const params = new URLSearchParams(window.location.search)
    const partner = params.get('partner')
    if (partner) localStorage.setItem('okoiya_partner', partner)
    supabase.from('admin_settings').select('value').eq('key', 'signups_paused').maybeSingle()
      .then(({ data }) => { if (data?.value?.enabled) setPaused(true) }, () => {})
  }, [])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  async function handleSignUp(e) {
    e.preventDefault()
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    setError(''); setLoading(true)
    try {
      const partnerSlug = localStorage.getItem('okoiya_partner')
      const data = await signUpWithEmail(email, password, partnerSlug ? { partner_slug: partnerSlug } : {})
      // If email confirmation is OFF, Supabase returns a live session — the user
      // is already signed in, so take them straight into onboarding. Otherwise
      // show the "check your email" screen.
      if (data?.session) navigate('/onboarding')
      else setSuccess(true)
    } catch (err) {
      setError(err.message || 'Sign up failed. Please try again.')
    } finally { setLoading(false) }
  }

  if (paused) return (
    <div style={{ minHeight: '100vh', background: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem', textAlign: 'center' }}>
      <div className="card" style={{ maxWidth: 420, width: '100%', padding: '2.5rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🌸</div>
        <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.4rem', margin: '0 0 0.75rem' }}>New signups are paused</h2>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.9rem', color: '#6B6B6B', lineHeight: 1.7, margin: '0 0 1.5rem' }}>
          We're temporarily not accepting new accounts. Please check back soon — and thank you for your patience.
        </p>
        <Link to="/" style={{ fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.875rem', color: '#C4826A', textDecoration: 'none' }}>← Back to home</Link>
      </div>
    </div>
  )

  if (success) return (
    <div style={{ minHeight: '100vh', background: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
      <div className="card" style={{ maxWidth: 420, width: '100%', padding: '2.5rem', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, background: 'rgba(155,175,155,0.15)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
          <Mail size={24} color="#9BAF9B" />
        </div>
        <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.5rem', marginBottom: '0.75rem' }}>Check your email</h2>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.875rem', color: '#6B6B6B', lineHeight: 1.7 }}>
          We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account, then come back to sign in.
        </p>
        <Link to="/login">
          <button className="btn-primary" style={{ marginTop: '1.5rem', width: '100%' }}>Back to Sign In</button>
        </Link>
      </div>
    </div>
  )

  return (
    <AuthShell image="joy-rings" tagline="Begin the most beautiful chapter." sub="One calm, beautiful place for every part of your wedding.">
      <Link to="/" style={{ textDecoration:'none', marginBottom:'2.5rem', display:'block' }}>
        <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:52, display:'block', margin:'0 auto' }}/>
      </Link>

      <div className="card" style={{ width: '100%', maxWidth: 420, padding: '2.5rem', boxShadow: '0 24px 64px rgba(122,60,40,0.10)' }}>
        <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.6rem', color: '#1A1A1A', marginBottom: '0.4rem', textAlign: 'center' }}>
          Begin your story
        </h2>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.875rem', color: '#6B6B6B', textAlign: 'center', marginBottom: '2rem' }}>
          Create your free Okoiya account
        </p>

        <button onClick={signInWithGoogle} style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
          background: 'white', border: '1.5px solid #E5E0D8', borderRadius: '0.75rem',
          padding: '0.75rem', fontFamily: 'Nunito Sans, sans-serif', fontWeight: 600, fontSize: '0.9rem',
          color: '#1A1A1A', cursor: 'pointer', marginBottom: '1.25rem'
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <div style={{ flex: 1, height: 1, background: '#E5E0D8' }} />
          <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.75rem', color: '#BCBCBC' }}>or</span>
          <div style={{ flex: 1, height: 1, background: '#E5E0D8' }} />
        </div>

        <form onSubmit={handleSignUp}>
          <div style={{ marginBottom: '1rem' }}>
            <label className="label">Your Full Name</label>
            <div style={{ position: 'relative' }}>
              <User size={15} color="#BCBCBC" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input className="input-field" style={{ paddingLeft: '2.25rem' }} type="text" placeholder="e.g. Adaeze Okonkwo" value={fullName} onChange={e => setFullName(e.target.value)} required />
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label className="label">Email Address</label>
            <div style={{ position: 'relative' }}>
              <Mail size={15} color="#BCBCBC" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input className="input-field" style={{ paddingLeft: '2.25rem' }} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label className="label">Password</label>
            <div style={{ position: 'relative' }}>
              <Lock size={15} color="#BCBCBC" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input className="input-field" style={{ paddingLeft: '2.25rem', paddingRight: '2.5rem' }} type={showPass ? 'text' : 'password'} placeholder="Min. 8 characters" value={password} onChange={e => setPassword(e.target.value)} required />
              <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {showPass ? <EyeOff size={15} color="#BCBCBC" /> : <Eye size={15} color="#BCBCBC" />}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ background: 'rgba(196,130,106,0.1)', border: '1px solid rgba(196,130,106,0.3)', borderRadius: '0.5rem', padding: '0.65rem 0.875rem', marginBottom: '1rem' }}>
              <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', color: '#A86650', margin: 0 }}>{error}</p>
            </div>
          )}

          <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Creating account…' : 'Create Free Account'}
          </button>
        </form>

        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.75rem', color: '#BCBCBC', textAlign: 'center', marginTop: '1rem', lineHeight: 1.6 }}>
          By signing up, you agree to our{' '}
          <a href="#" style={{ color: '#9BAF9B' }}>Terms of Use</a> and{' '}
          <a href="#" style={{ color: '#9BAF9B' }}>Privacy Policy</a>
        </p>

        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', color: '#6B6B6B', textAlign: 'center', marginTop: '1rem' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#C4826A', fontWeight: 600, textDecoration: 'none' }}>Sign in</Link>
        </p>
      </div>
    </AuthShell>
  )
}
